import React, { useRef } from "react";
import { motion } from "framer-motion";
import { Reveal, StaggerGroup, StaggerChild } from "@/components/site/Reveal";
import AnimatedCounter from "./AnimatedCounter";

const OUTCOMES = [
    {
        label: "Inbound support handled by Nyra",
        stat: "50-60%",
        arrow: "up",
        vertical: "Enterprise SaaS",
        blurb: "Enterprise teams use Nyra AI as a single front door for feedback, support, knowledge base, and first-line queries — plugged into ticketing, CRM and product-roadmap tools.",
        mesh: "",
    },
    {
        label: "Setup and migration",
        stat: "<1 day",
        arrow: "up",
        vertical: "Customer Experience",
        blurb: "Teams import contacts and help-centre articles in minutes and move support, outreach and internal channels into one Nyra-powered workspace in under a day.",
        mesh: "mesh-b",
    },
    {
        label: "Customer inquiries handled by AI",
        stat: "50%",
        arrow: "up",
        vertical: "Financial Services",
        blurb: "Businesses replace siloed helpdesk setups with an integrated Nyra AI chat + knowledge base — resolving nearly half of inbound inquiries autonomously.",
        mesh: "mesh-c",
    },
];

export const RealOutcomes = () => (
    <section className="py-16 md:py-20 bg-mint" data-testid="real-outcomes">
        <div className="container-x">
            <Reveal>
                <h2 className="heading-section text-2xl md:text-3xl mb-10">Real outcomes with Nyra AI</h2>
            </Reveal>

            <StaggerGroup className="grid md:grid-cols-3 gap-4">
                {OUTCOMES.map((o, i) => (
                    <StaggerChild key={i}>
                        <div className={`mesh-frame ${o.mesh} min-h-[280px] flex flex-col justify-between`} data-testid={`outcome-${i}`}>
                            <div>
                                <div className="text-slate-800 text-sm font-medium mb-2">{o.label}</div>
                                <p className="text-slate-600 text-xs leading-relaxed max-w-xs">{o.blurb}</p>
                            </div>
                            <div className="flex items-end justify-between mt-6">
                                <div className="flex items-baseline gap-1">
                                    <span
                                        className="heading-stat text-4xl md:text-5xl text-slate-900"
                                    >
                                        {o.stat}
                                    </span>
                                    <i className="fa-solid fa-arrow-up text-slate-700 text-lg" />
                                </div>
                                <div
                                    className="text-xs font-medium text-slate-600 uppercase"
                                    style={{ letterSpacing: "0.1em" }}
                                >
                                    {o.vertical}
                                </div>
                            </div>
                        </div>
                    </StaggerChild>
                ))}
            </StaggerGroup>
        </div>
    </section>
);

/* ------------------------------------------------------------------
   COMPANY STATEMENT — big serif line + inline stats (Botpress-style)
------------------------------------------------------------------- */
export const CompanyStatement = () => (
    <section className="pt-24 md:pt-28 pb-8 md:pb-10 bg-warm" data-testid="company-statement">
        <div className="container-x">
            <div className="grid lg:grid-cols-12 gap-x-12 gap-y-10 items-start">
                <div className="lg:col-span-7">
                    <Reveal>
                        <div className="text-eyebrow text-slate-500 mb-4">
                            <span className="w-6 h-px inline-block align-middle bg-slate-400 mr-2" />
                            Who we are
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="heading-hero text-4xl sm:text-5xl md:text-[3.4rem] leading-[1.08]">
                            An enterprise AI company that built a<br />
                            <span className="italic">real assistant</span>.
                            <br />
                            <span className="muted">Not just another chatbot.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-5 text-intro text-slate-600 text-base md:text-lg max-w-xl">
                            A decade of enterprise AI engineering, distilled into one
                            <span className="italic"> agentic</span> platform — governed, grounded and
                            production-ready from day one.
                        </p>
                    </Reveal>
                </div>
                <div className="lg:col-span-5">
                    <Reveal delay={0.2}>
                        <div className="rounded-3xl border border-slate-200/80 bg-white/70 backdrop-blur-sm p-6 md:p-8"
                            style={{ boxShadow: "0 4px 20px -12px rgba(35,95,82,0.10)" }}>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-8">
                                {[
                                    { n: "50+",  l: "Enterprise deployments" },
                                    { n: "4+",   l: "Years of AI infrastructure" },
                                    { n: "22+",  l: "Languages supported" },
                                    { n: "80%",  l: "Auto-resolution on complex tickets" },
                                ].map((s, i) => (
                                    <div key={i} className="flex flex-col">
                                        <div className="heading-stat text-4xl md:text-5xl text-slate-900">
                                            {s.n}
                                        </div>
                                        <div className="mt-2 text-xs md:text-sm text-slate-600 leading-snug">
                                            {s.l}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </div>
    </section>
);


const STAT_HIGHLIGHTS = [
    {
        brand: "Distance-Learning Institute",
        stat: "60%",
        label: "counsellor load reduction",
        detail: "AI Voice · Distance Education",
        icon: "fa-solid fa-phone-volume",
        accent: "#2E7D6B",
    },
    {
        brand: "Consumer Fintech Platform",
        stat: "3×",
        label: "credit issue resolution",
        detail: "WhatsApp AI · Fintech",
        icon: "fa-brands fa-whatsapp",
        accent: "#5FA88A",
    },
    {
        brand: "Nyra Enterprise",
        stat: "24×7",
        label: "always-on operations",
        detail: "Omnichannel · Multi-industry",
        icon: "fa-solid fa-clock-rotate-left",
        accent: "#235F52",
    },
];

export const StatHighlights = () => (
    <section className="py-20 md:py-24 bg-clean" data-testid="stat-highlights">
        <div className="container-x">
            <Reveal>
                <div className="text-eyebrow text-slate-500 mb-4 text-center">
                    Trusted by businesses across the world
                </div>
                <h2 className="heading-section text-3xl md:text-5xl text-center mb-12">
                    Real deployments. <span className="italic">Real numbers.</span>
                </h2>
            </Reveal>
            <StaggerGroup className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {STAT_HIGHLIGHTS.map((s) => (
                    <StaggerChild key={s.brand}>
                        <motion.div
                            whileHover={{ y: -4 }}
                            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                            className="h-full rounded-3xl p-8 md:p-9 border border-slate-200 bg-white flex flex-col justify-between relative overflow-hidden group"
                            style={{ boxShadow: "0 4px 12px -6px rgba(35,95,82,0.06)" }}
                            data-testid={`stat-${s.brand.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            <div
                                className="absolute top-0 left-0 h-1 transition-all duration-500 group-hover:w-full"
                                style={{
                                    width: "56px",
                                    background: `linear-gradient(90deg, ${s.accent}, ${s.accent}00)`,
                                }}
                            />
                            <div className="flex items-center justify-between mb-8">
                                <div className="text-xs uppercase tracking-[0.2em] text-slate-700 font-medium">
                                    {s.brand}
                                </div>
                                <div
                                    className="icon-sticker w-11 h-11"
                                    style={{ "--st-color": s.accent }}
                                >
                                    <i className={`${s.icon} text-base`} />
                                </div>
                            </div>
                            <div>
                                <div
                                    className="heading-stat text-6xl md:text-7xl tabular-nums text-slate-900"
                                >
                                    {s.stat}
                                </div>
                                <div className="mt-3 text-slate-700 text-base leading-snug">
                                    {s.label}
                                </div>
                                <div className="mt-6 pt-6 border-t border-slate-100 text-xs text-slate-500">
                                    {s.detail}
                                </div>
                            </div>
                        </motion.div>
                    </StaggerChild>
                ))}
            </StaggerGroup>
            <Reveal delay={0.3}>
                <div className="mt-10 text-center">
                    <a
                        href="/customer-stories"
                        className="text-sm text-slate-700 hover:text-slate-900 inline-flex items-center gap-2 font-medium transition-colors"
                    >
                        Read all customer stories
                        <i className="fa-solid fa-arrow-right text-xs" />
                    </a>
                </div>
            </Reveal>
        </div>
    </section>
);

const METRICS = [
    { stat: 22, suffix: "+", label: "Indian & international languages across voice & chat", icon: "fa-solid fa-language", accent: "#2E7D6B" },
    { stat: 7, suffix: "+", label: "Channels — WhatsApp, web, voice, IVR & more", icon: "fa-solid fa-network-wired", accent: "#5FA88A" },
    { stat: 80, suffix: "%", label: "Queries resolved without a human agent", icon: "fa-solid fa-check-double", accent: "#235F52" },
    { stat: 90, suffix: "%", label: "Faster tender review vs. manual evaluation", icon: "fa-solid fa-bolt", accent: "#2E7D6B" },
];

export const MetricsBar = () => (
    <section className="py-16 md:py-20 bg-cool" data-testid="metrics-bar">
        <div className="container-x">
            <Reveal>
                <div className="mb-10 md:mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <div className="max-w-2xl">
                        <div className="text-eyebrow text-slate-500 mb-3">By the numbers</div>
                        <h2 className="heading-section text-3xl md:text-4xl">
                            Enterprise-scale AI, <span className="italic">measured in outcomes.</span>
                        </h2>
                    </div>
                    <div className="text-sm text-slate-500 md:text-right">
                        Live across Banking, Insurance, Healthcare,<br className="hidden md:inline" />
                        Retail, Manufacturing & Government.
                    </div>
                </div>
            </Reveal>

            <Reveal delay={0.1}>
                <div
                    className="relative rounded-3xl overflow-hidden border border-slate-200"
                    style={{
                        background:
                            "linear-gradient(135deg, #ffffff 0%, #F5FAFF 50%, #EEF8F3 100%)",
                        boxShadow: "0 30px 60px -30px rgba(30, 58, 95, 0.18)",
                    }}
                >
                    {/* decorative orbs */}
                    <div
                        className="absolute -top-16 -right-16 w-64 h-64 rounded-full pointer-events-none"
                        style={{ background: "radial-gradient(circle, #2E7D6B22, transparent 70%)" }}
                    />
                    <div
                        className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full pointer-events-none"
                        style={{ background: "radial-gradient(circle, #5FA88A22, transparent 70%)" }}
                    />

                    <div className="relative grid grid-cols-2 md:grid-cols-4">
                        {METRICS.map((m, i) => (
                            <StaggerChild key={i}>
                                <div
                                    className="group relative p-7 md:p-9 flex flex-col"
                                    style={{
                                        borderRight:
                                            i < METRICS.length - 1
                                                ? "1px dashed rgba(30,58,95,0.15)"
                                                : "none",
                                        borderBottom:
                                            i < 2
                                                ? "1px dashed rgba(30,58,95,0.15)"
                                                : "none",
                                    }}
                                    data-testid={`metric-${i}`}
                                >
                                    <div className="flex items-center gap-3 mb-5">
                                        <div
                                            className="icon-sticker w-11 h-11"
                                            style={{ "--st-color": m.accent }}
                                        >
                                            <i className={`${m.icon} text-lg`} />
                                        </div>
                                        <div
                                            className="h-px flex-1"
                                            style={{
                                                background: `linear-gradient(90deg, ${m.accent}55, transparent)`,
                                            }}
                                        />
                                    </div>
                                    <div
                                        className="heading-stat text-6xl md:text-7xl text-slate-900"
                                    >
                                        <AnimatedCounter to={m.stat} suffix={m.suffix} />
                                    </div>
                                    <div className="mt-4 text-slate-600 text-sm leading-relaxed max-w-[240px]">
                                        {m.label}
                                    </div>
                                </div>
                            </StaggerChild>
                        ))}
                    </div>
                </div>
            </Reveal>
        </div>
    </section>
);


export const MarqueeHeadline = () => {
    const items = [
        "Conversational AI",
        "Autonomous Agents",
        "Multi-Agent Orchestration",
        "Tender Intelligence",
        "Enterprise-Ready",
        "Multilingual",
    ];
    return (
        <section
            className="relative py-12 overflow-hidden bg-deep"
            data-testid="marquee-headline"
        >
            <div className="marquee">
                <div
                    className="logo-marquee"
                    style={{ animationDuration: "38s", gap: "3rem" }}
                >
                    {[...items, ...items, ...items].map((t, i) => (
                        <div key={i} className="flex items-center gap-8">
                            <span
                                className="heading-hero text-4xl md:text-6xl text-white whitespace-nowrap"
                            >
                                {t}
                            </span>
                            <span
                                className="w-4 h-4 rounded-full shrink-0"
                                style={{ background: "#5FA88A" }}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

